import { Box, Radio } from '@wix/design-system';
import { CycleUtils, GEOS, wixServerData } from '@wix/package-picker-shared';
import {
  CycleSelectorBadge,
  CycleSelectorItemName,
  RevShareComponents,
  STUDIO_CARD_BORDER_COLOR,
  useCycleAvailability,
  useIsSeasonalSale,
  useIsStudioNewUi,
  useLocalization,
  useMonthlyDisabledFeatures,
  useRevShare,
} from '@wix/package-picker-shared-studio';
import { CycleSelector as PlatformCycleSelector } from '@wix/premium-purchase-platform/@components';
import { PurchaseOffering } from '@wix/premium-purchase-platform/@sdk';
import React from 'react';
import { StudioPackagePickerModule } from '../../modules/studio-package-picker.module';
import { BrazilCyclePrice } from './BrazilCyclePrice';
import s from './CycleSelector.scss';
import { CYCLE_SELECTOR_DATA_HOOKS } from './CycleSelector.datahooks';
import { CycleSelectorCheck } from './CycleSelectorCheck';
import { CycleSelectorDisabledFeatures } from './CycleSelectorDisabledFeatures';
import { useCycleSelectorItemTooltip } from './useCycleSelectorItemTooltip';

interface CycleSelectorItemProps {
  idx: number;
  isSelected: boolean;
  pricingDetail: PurchaseOffering.PricingDetails;
  productId: string;
  selectCycle: (cycle: PurchaseOffering.PurchaseCycle) => void;
}

const getCycleSavings = ({
  hasSalePrice,
  formattedSaleDiscount,
  isSeasonalSale,
  showRevShareSavings,
  pricingDetail,
  formatSavings,
}: {
  hasSalePrice: boolean;
  formattedSaleDiscount: string | undefined;
  isSeasonalSale: boolean;
  showRevShareSavings: boolean;
  pricingDetail: PurchaseOffering.PricingDetails;
  formatSavings: (amount?: string) => string;
}): string | undefined => {
  if (hasSalePrice) {
    return formattedSaleDiscount;
  }
  if (!isSeasonalSale && showRevShareSavings) {
    return formatSavings(pricingDetail.savings?.fixedAmount?.amount);
  }
  return undefined;
};

export const CycleSelectorItem = ({
  idx,
  isSelected,
  pricingDetail,
  productId,
  selectCycle,
}: CycleSelectorItemProps) => {
  const cycle = pricingDetail.cycle;
  const formatPrice = StudioPackagePickerModule.SDK.useFormatPrice();
  const { shouldShowJapaneseExperience } = useLocalization();
  const { experiments } =
    StudioPackagePickerModule.internals.experiments.hooks.useExperiments();
  const isBrazilCyclePrice =
    experiments.isEnabled('specs.premium.BrazilCyclePricing') &&
    wixServerData.get().geo === GEOS.BRAZIL &&
    cycle &&
    !CycleUtils.isMonthly(cycle);
  useMonthlyDisabledFeatures(productId);
  const tooltipData = useCycleSelectorItemTooltip({
    cycle,
    productId,
  });
  const currentProductQuery =
    StudioPackagePickerModule.SDK.switchApiClient.hooks.useGetCurrentProduct(
      {},
    );
  const { data: product } =
    StudioPackagePickerModule.SDK.studioOfferingApiClient.hooks.useFetchProduct(
      { productId },
    );
  const { getRevShareAmountPerCycle } = useRevShare();

  const { isCycleAvailable } = useCycleAvailability(
    productId,
    cycle || undefined,
  );

  const isSeasonalSale = useIsSeasonalSale();
  const isStudioNewUi = useIsStudioNewUi();

  if (!cycle || currentProductQuery.isLoading) {
    return null;
  }

  const isMonthly =
    cycle?.cycleDuration?.unit === PurchaseOffering.DurationUnit.MONTH &&
    cycle?.cycleDuration?.count === 1;

  const showRevShareSavings = getRevShareAmountPerCycle(cycle) > 0;

  const saleDiscountAmount = Number(
    pricingDetail.finalPrice?.discount?.fixedAmount?.amount,
  );
  const formattedSaleDiscount =
    saleDiscountAmount > 0
      ? formatPrice(saleDiscountAmount, { hideDecimalPlaces: true })
      : undefined;

  const cycleId = cycle.cycleDuration
    ? CycleUtils.convertCycleToCycleId(cycle)
    : undefined;
  const initialMonthlyPrice =
    pricingDetail.initialPurchasePrice?.monthlyTotal?.formattedAmount;
  const finalMonthlyPrice =
    pricingDetail.finalPrice?.monthlyTotal?.formattedAmount;
  const hasSalePrice = Boolean(
    isSeasonalSale && cycleId && product?.salePerCycle?.[cycleId],
  );

  const cycleCard = (
    <PlatformCycleSelector.Card.Root
      dataHook={CYCLE_SELECTOR_DATA_HOOKS.item}
      padding={isStudioNewUi ? '19px 20px' : 'SP4'}
      key={idx}
      isSelected={isSelected}
      isOpen={isSelected}
      backgroundColor="white"
      disabled={!isCycleAvailable}
      disabledBackgroundColor="white"
      onSelect={() => selectCycle(cycle)}
      collapseContent={
        <RevShareComponents.CollapsibleCycleWrapper
          cycle={cycle}
          shouldRenderChildren={isMonthly}
          sectionHelperProps={
            isStudioNewUi
              ? { backgroundColor: 'transparent', padding: '0 0 0 28px' }
              : undefined
          }
        >
          <CycleSelectorDisabledFeatures
            productId={productId}
            selectCycle={selectCycle}
          />
        </RevShareComponents.CollapsibleCycleWrapper>
      }
      {...(isStudioNewUi
        ? {
            borderRadius: '8px',
            borderColor: STUDIO_CARD_BORDER_COLOR,
            className: isSelected ? s.selectedCard : undefined,
          }
        : { borderTop: 'none' })}
    >
      <Box
        direction="horizontal"
        gap="SP2"
        verticalAlign={isStudioNewUi ? 'middle' : undefined}
      >
        {isStudioNewUi ? (
          <CycleSelectorCheck isSelected={isSelected} size="16px" />
        ) : (
          <Radio checked={isSelected} disabled={!isCycleAvailable} />
        )}
        <PlatformCycleSelector.Card.Content
          dataHook="cycle-selector-content"
          width="100%"
          opacity={!isCycleAvailable ? 0.3 : 1}
          display="grid"
          gridTemplateColumns={
            isStudioNewUi ? '15% 1fr max-content' : '15% 2% 1fr max-content'
          }
          gap={isStudioNewUi ? '6px' : '4px'}
        >
          <CycleSelectorItemName
            dataHook={CYCLE_SELECTOR_DATA_HOOKS['item-name']}
            tooltipDataHook={CYCLE_SELECTOR_DATA_HOOKS['item-name-tooltip']}
            cycle={cycle}
            showInMonths={shouldShowJapaneseExperience}
            textProps={
              isStudioNewUi ? { className: s.cycleName } : { weight: 'bold' }
            }
            tooltipContent={tooltipData?.tooltipContent}
            onShowTooltip={tooltipData?.onShowTooltip}
          />

          {!isStudioNewUi && (
            <PlatformCycleSelector.Card.Separator height="1px" width="8px" />
          )}

          {isBrazilCyclePrice ? (
            <BrazilCyclePrice productId={productId} cycle={cycle} />
          ) : (
            <PlatformCycleSelector.Card.Price
              price={finalMonthlyPrice ?? undefined}
              fullPrice={
                hasSalePrice ? initialMonthlyPrice ?? undefined : undefined
              }
              durationCount={pricingDetail.cycle?.cycleDuration?.count}
              durationUnit={pricingDetail.cycle?.cycleDuration?.unit}
              cycleSavings={getCycleSavings({
                hasSalePrice,
                formattedSaleDiscount,
                isSeasonalSale,
                showRevShareSavings,
                pricingDetail,
                formatSavings: (amount) =>
                  formatPrice(Number(amount), { hideDecimalPlaces: true }),
              })}
              cycleSavingsTextProps={
                hasSalePrice
                  ? { className: s.saleCycleSavings }
                  : isStudioNewUi
                  ? { className: s.cycleSavings }
                  : undefined
              }
              {...(isStudioNewUi
                ? {
                    gap: '4px',
                    priceDurationCountBoxProps: {
                      gap: '4px',
                      display: isMonthly ? 'none' : 'flex',
                    },
                  }
                : {})}
            />
          )}

          <Box width="100%" align="right">
            {isCycleAvailable && (
              <CycleSelectorBadge
                dataHook={CYCLE_SELECTOR_DATA_HOOKS.badge}
                tooltipDataHook={CYCLE_SELECTOR_DATA_HOOKS['badge-tooltip']}
                productId={productId}
                cycle={cycle}
                isSelected={isSelected}
                shouldShowTooltip={!!currentProductQuery.data?.currentProduct}
                onShowTooltip={(content) => {
                  StudioPackagePickerModule.internals.bi.onCycleSavingTooltipHover?.(
                    {
                      content,
                    },
                  );
                }}
              />
            )}
          </Box>
        </PlatformCycleSelector.Card.Content>
      </Box>
    </PlatformCycleSelector.Card.Root>
  );

  return cycleCard;
};
